import { useState } from 'react';
import { Container, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { BoxArrowRight, People, PersonCircle } from 'react-bootstrap-icons';
import { useAuth } from '@/contexts/AuthContext';
import NumaLogo from '@/assets/numa-logo.svg?react';
import './NavigationBar.css';

interface NavItem {
  to: string;
  label: string;
  /** Home matches its own path only. Every other link also matches the pages under it. */
  exact?: boolean;
}

/** The day-to-day pages, in the order a release moves: see the fleet, change a client, ship it. */
const MAIN: NavItem[] = [
  { to: '/', label: 'Home', exact: true },
  { to: '/numa-dashboard', label: 'Dashboard' },
  { to: '/numa-credits', label: 'Credits' },
  { to: '/configs', label: 'Configs' },
  { to: '/client-checks', label: 'Client checks' },
  { to: '/deployments', label: 'Deployments' },
  { to: '/containers', label: 'Images' },
  { to: '/tools', label: 'Tools' },
];

/** Reference pages, kept quieter on the right. */
const REFERENCE: NavItem[] = [
  { to: '/docs', label: 'Docs' },
  { to: '/activity', label: 'Activity' },
];

export default function NavigationBar() {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [expanded, setExpanded] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const close = () => setExpanded(false);
  const isActive = (item: NavItem) =>
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const renderLink = (item: NavItem) => (
    <Nav.Link key={item.to} as={Link} to={item.to} active={isActive(item)} onClick={close}>
      {item.label}
    </Nav.Link>
  );

  return (
    <Navbar expand="lg" collapseOnSelect expanded={expanded} onToggle={setExpanded} className="portal-nav" sticky="top">
      <Container fluid>
        <Navbar.Brand as={Link} to="/" onClick={close}>
          <NumaLogo />
          <span>Numa Customer Portal</span>
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="portal-nav-links" />

        <Navbar.Collapse id="portal-nav-links">
          <Nav className="me-auto portal-nav__main">{MAIN.map(renderLink)}</Nav>

          <Nav className="portal-nav__side">
            {REFERENCE.map(renderLink)}

            <NavDropdown
              title={
                <span className="portal-nav__account">
                  <PersonCircle size={18} />
                  <span>{user?.username || 'Account'}</span>
                </span>
              }
              id="portal-nav-account"
              align="end"
              className="user-dropdown"
            >
              <NavDropdown.ItemText>
                <div className="portal-nav__who">{user?.username}</div>
                {user?.email && user.email !== user.username && <div className="portal-nav__email">{user.email}</div>}
              </NavDropdown.ItemText>
              <NavDropdown.Divider />
              <NavDropdown.Item as={Link} to="/users" onClick={close}>
                <People size={15} className="me-2" />
                Portal users
              </NavDropdown.Item>
              <NavDropdown.Item onClick={handleSignOut}>
                <BoxArrowRight size={15} className="me-2" />
                Sign out
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}
